import { Box, FlexBox, Rhythm, Text, TextSmall } from '@trmediaab/zebra';
import { Html, Money } from '@trmediaab/zebra-components';
import { Info } from '@trmediaab/zebra-icons';

import type { CheckoutState } from '../../state';
import { useImmutableState } from '../../state';
import { isShippingOrder } from '../../utils/orderUtils';
import useOrderPresentation from '../../utils/useOrderPresentation';
import Card from '../Card';
import ErrorMessage from '../ErrorMessage';
import { SelectInput } from '../Form';
import Layout from '../Layout';
import TextWithAdjacentIcon from '../TextWithAdjacentIcon';

export interface ProductLineitemsCardProps {
  order: NonNullable<CheckoutState['order']>;
  allowQuantityChange: boolean;
  onChangeQuantity: (
    ev: React.ChangeEvent<HTMLSelectElement>,
    idx: number,
  ) => void;
}

const ProductLineitemsCard = ({
  order,
  allowQuantityChange,
  onChangeQuantity,
}: ProductLineitemsCardProps) => {
  const hasShipping = isShippingOrder(order);
  const { appMode } = useImmutableState();
  const lineItemPresentations = useOrderPresentation();

  if (lineItemPresentations == null || lineItemPresentations.length === 0) {
    return (
      <Layout.area area="products">
        <ErrorMessage error={new Error('Okänt fel, försök igen')} />
      </Layout.area>
    );
  }

  return (
    <Layout.area area="products">
      <Card
        fillHeight={hasShipping}
        header={appMode === 'redeemGift' ? 'VALD GÅVOPRENUMERATION' : undefined}
      >
        {lineItemPresentations.map((presentation, idx) => {
          const {
            headline,
            description,
            terms,
            discount,
            showPrice,
            updatedPrice,
            unitPrice,
            quantity,
            maxQuantity,
            id,
          } = presentation;

          return (
            <Rhythm between="3" key={id}>
              <Text
                display="grid"
                lineHeight="medium"
                sx={{ gridTemplateColumns: 'auto auto' }}
              >
                <Text fontWeight="bold" fontSize={{ md: 3 }} mr="3">
                  {headline}
                </Text>
                {showPrice && (
                  <Text ml="auto" whiteSpace="nowrap" fontSize={{ md: 3 }}>
                    <Money
                      value={unitPrice}
                      fontWeight={discount ? 'semiBold' : 'bold'}
                      textDecoration={discount ? 'line-through' : undefined}
                    />
                    {discount && (
                      <>
                        {' '}
                        <Money
                          value={unitPrice - discount}
                          color="checkout.discount"
                          fontWeight="extraBold"
                        />
                      </>
                    )}
                  </Text>
                )}
              </Text>
              <Html between="2" variant="checkoutSmall" html={description} />
              <TextSmall display="block" color="text.faded">
                {terms}
              </TextSmall>
              {allowQuantityChange && maxQuantity > 1 && (
                <FlexBox alignItems="center">
                  <Text.strong>Välj antal</Text.strong>
                  <Box ml="auto">
                    <SelectInput
                      name="quantity"
                      value={quantity}
                      data={Array.from({ length: maxQuantity }).map((_, v) => ({
                        value: v + 1,
                        label: (v + 1).toString(),
                      }))}
                      onChange={ev => onChangeQuantity(ev, idx)}
                    />
                  </Box>
                </FlexBox>
              )}
              {showPrice && updatedPrice && (
                <TextWithAdjacentIcon
                  icon={<Info color="checkout.icon1" size="16px" />}
                  text="Baserat på momsreglerna i ditt bostadsland har ditt
                      slutgiltiga pris justerats"
                  gap="1,5"
                  backgroundColor="checkout.infoBg"
                  p="2,5"
                  as={TextSmall}
                />
              )}
            </Rhythm>
          );
        })}
      </Card>
    </Layout.area>
  );
};

export default ProductLineitemsCard;
